import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../Button';
import { ThemeToggle } from './ThemeToggle';
import { useTheme } from '../../context/ThemeContext';
import ReactGA from 'react-ga4';

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '#' },
    { name: 'Services', href: '#services' },
    { name: 'Projects', href: '#projects' },
    { name: 'How We Work', href: '#process' },
  ];

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);

    if (location.pathname !== '/') {
      // Navigate home first, then scroll
      navigate('/');
      if (href !== '#') {
        setTimeout(() => {
          document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      }
    } else {
      if (href === '#') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // On inner pages, always show scrolled (solid) nav
  const isInnerPage = location.pathname !== '/';
  const showScrolledStyle = isScrolled || isInnerPage;

  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        showScrolledStyle
          ? isDark
            ? 'bg-[#111827]/95 backdrop-blur-sm shadow-sm shadow-black/20 py-3'
            : 'bg-white/95 backdrop-blur-sm shadow-sm py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div className="flex-shrink-0 flex items-center gap-3.5">
            <img 
              src="/logo.png" 
              alt="Harmony Home Designs Logo" 
              fetchPriority="high"
              loading="eager"
              className={`w-11 h-11 object-contain transition-all hover:scale-105 hover:drop-shadow-[0_0_8px_rgba(201,162,107,0.4)] duration-300 ${
                !isDark && showScrolledStyle ? 'brightness-90 contrast-125' : ''
              }`} 
            />
            <div className="flex flex-col justify-center">
              <a
                href="#"
                onClick={(e) => handleNavClick(e, '#')}
                className={`font-poppins font-bold text-[1.4rem] tracking-tight transition-colors leading-none ${
                  showScrolledStyle
                    ? isDark ? 'text-white' : 'text-slate-900'
                    : 'text-white'
                }`}
              >
                AURA<span className="text-accent">SPACES</span>
              </a>
              <span className={`text-[0.6rem] font-semibold tracking-[0.15em] uppercase mt-1.5 transition-colors ${
                showScrolledStyle
                  ? isDark ? 'text-gray-400' : 'text-slate-500'
                  : 'text-white/80'
              }`}>
                by Harmony Home Designs
              </span>
            </div>
          </div>
          
          <nav aria-label="Main navigation" className="hidden md:flex space-x-8">
            {navLinks.map((link) => (
              <a 
                key={link.name} 
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`font-medium transition-colors text-sm uppercase tracking-wider ${
                  showScrolledStyle
                    ? isDark
                      ? 'text-gray-300 hover:text-accent'
                      : 'text-primary hover:text-accent'
                    : 'text-white/90 hover:text-white'
                }`}
              >
                {link.name}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <ThemeToggle
              className={
                showScrolledStyle
                  ? isDark ? 'text-gray-300 hover:text-accent' : 'text-primary hover:text-accent'
                  : 'text-white hover:text-accent'
              }
            />
            <Button variant={showScrolledStyle ? (isDark ? "accent" : "primary") : "secondary"} size="sm" className={!showScrolledStyle ? "bg-white text-slate-900 font-semibold hover:bg-gray-50 border-none shadow-md" : ""} onClick={() => { ReactGA.event({ category: 'CTA', action: 'Clicked Contact Us Nav' }); document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' }); }}>
              Contact Us
            </Button>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <ThemeToggle
              className={
                showScrolledStyle
                  ? isDark ? 'text-gray-300' : 'text-primary'
                  : 'text-white'
              }
            />
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`${showScrolledStyle ? (isDark ? 'text-white' : 'text-primary') : 'text-white'} hover:text-accent focus:outline-none`}
              aria-label="Toggle Navigation Menu"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <nav aria-label="Mobile navigation" className={`md:hidden shadow-xl absolute top-full left-0 w-full border-t ${
          isDark
            ? 'bg-[#111827] border-gray-700'
            : 'bg-white border-gray-100'
        }`}>
          <div className="px-4 pt-2 pb-6 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className={`block px-3 py-4 text-base font-medium uppercase border-b ${
                  isDark
                    ? 'text-gray-200 hover:text-accent hover:bg-white/5 border-gray-700'
                    : 'text-primary hover:text-accent hover:bg-gray-50 border-gray-50'
                }`}
                onClick={(e) => handleNavClick(e, link.href)}
              >
                {link.name}
              </a>
            ))}
            <div className="pt-4 px-3">
              <Button variant="primary" className="w-full" onClick={() => document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' })}>
                Get Free Consultation
              </Button>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}
